/**
 * FieldGroupModal (Unit 5 Phase 3b) — two-level grouping editor.
 *
 * A STRUCTURAL CLONE OF FieldSortModal, and deliberately so: same three-band
 * chrome (fixed header, flex:1 scrolling body, fixed footer), same working-copy
 * transaction, same Escape ownership, same props shape. A rep who has used Sort
 * has used this. The differences below are the only ones.
 *
 * NO ModalShell, for the reason the Sort and Columns modals already record:
 * that shared component exposes only title / children / footer, has no header
 * slot, and is shared with the warehouse and inventory modals, so widening it
 * would put those in this unit's blast radius.
 *
 * WORKING COPY:  copy on open → edit the draft → Apply commits → Cancel/✕
 * discards. Mount IS open (the host mount-gates on tmgFieldGroupOpen), so the
 * initial copy is a lazy useState initialiser and there is no open-transition
 * effect to keep in sync.
 *
 * CLEAR WIPES THE DRAFT, NOT THE STORED GROUPING, and does not close (D-117).
 * It is an edit like any other — the rep still taps Apply to commit an empty
 * grouping, or Cancel to keep what they had. Bob's prototype clears live; we do
 * not, because making Clear the one destructive control outside the transaction
 * the other three are inside is exactly the inconsistency the working-copy
 * pattern exists to avoid. Sort and Filter already rule this way.
 *
 * NO "KEEP GROUPED" TOGGLE (D-110). Bob declares one, assigns it, persists it
 * and reads it back at zero sites that do anything with it — the same dead
 * chrome as his "keep sorted", omitted here deliberately rather than ported and
 * left equally dead.
 *
 * NO DIRECTION CONTROL. A sort level carries asc/desc because the rep chose an
 * ordering; a group does not. Bucket order is the collator with blanks last,
 * decided by groupRecords, so that Sort and Group cannot disagree about where
 * an empty cell belongs.
 *
 * TWO FIXED SLOTS, NOT A LIST. Sort renders a variable-length array with add
 * and remove controls because its length is the rep's decision. Grouping is
 * capped at two named slots, so there is no "+ Add" control and no ✕ per row —
 * a slot is emptied by choosing its own unset option, the same '' ⇄ null
 * semantics the record form's sheet uses.
 *
 * SECONDARY IS UNREACHABLE UNTIL PRIMARY IS SET, not disabled. Secondary
 * without primary is not a state the storage layer will even accept, and the
 * zone's standing rule is that a greyed control on a phone reads as broken,
 * invites a tap that does nothing, and still costs its height. So the row is
 * simply not rendered.
 *
 * SECONDARY CANNOT NAME PRIMARY. Its picker is handed the `exclude` prop, so
 * the field already chosen above is absent from the list rather than sitting in
 * it as a trap. Two guards, because the picker alone is not enough: choosing a
 * NEW primary that matches the current secondary clears the secondary in the
 * same setDraft, since the offending pair is created from the other side.
 *
 * ⚠ THE PICKER GETS `value={draft.primary}`, THE <select> GETS `?? ''`. They
 * differ on purpose. The <select> needs a string or React drops it to
 * uncontrolled; the picker's `value` drives its escape-hatch option, which
 * fires for any non-null value it does not recognise — handed '' it would emit
 * a second, blank-labelled option next to the real unset row. Passing the raw
 * null suppresses the hatch exactly when there is nothing to escape.
 *
 * CSS: every class here is a shipped .fsm-* rule and this file adds none. The
 * stylesheet is imported by name rather than inherited from FieldSortModal's
 * own import — the dependency is real, so it is declared. This is the one place
 * the zone's one-component-one-namesake-stylesheet convention is bent, and it
 * is bent rather than duplicating a stylesheet that would then drift.
 */

import React, { useEffect, useState } from 'react';
import FieldPickerOptions, { firstPickerKey } from './FieldPickerOptions';
import './FieldSortModal.css';

/* Stable empty defaults, module scope and never mutated — the same reasoning as
   FieldSortModal's: a missing prop must render an empty picker, not throw on
   .has() or .filter(). */
const EMPTY_FIELDS = [];
const EMPTY_HIDDEN = new Set();

/** The unset sentinel in both <select>s. '' ⇄ null, as everywhere in the zone. */
const UNSET = '';

export default function FieldGroupModal({
  fields = EMPTY_FIELDS, hidden = EMPTY_HIDDEN, group, onApply, onClose,
}) {
  // THE WORKING COPY. Rebuilt from the two meaningful slots rather than spread,
  // so a stored object carrying extra keys cannot ride into the draft and back
  // out through Apply.
  const [draft, setDraft] = useState(() => ({
    primary: group && typeof group.primary === 'string' ? group.primary : null,
    secondary: group && typeof group.secondary === 'string' ? group.secondary : null,
  }));

  // The data precondition, same shape as Sort's: with nothing offerable there
  // is nothing a grouping could name, so the controls do not render at all.
  const firstKey = firstPickerKey(fields, hidden);

  // Escape (desktop) — capture + stopPropagation, this modal only. The View
  // panel underneath stays mounted and still listening, and two capture-phase
  // listeners on `window` both fire, so each editor owns Escape for itself
  // while it is up and the host gates the panel's listener off.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // TWO collapses, both required, both in one setDraft so no render sits
  // between them:
  //   primary cleared      → secondary goes too (not a state)
  //   primary takes over the secondary's field → secondary goes (not a level)
  const changePrimary = (next) => {
    setDraft((prev) => {
      const primary = next === UNSET ? null : next;
      const secondary = (primary === null || prev.secondary === primary)
        ? null
        : prev.secondary;
      return { primary, secondary };
    });
  };

  const changeSecondary = (next) => {
    setDraft((prev) => ({ ...prev, secondary: next === UNSET ? null : next }));
  };

  // Draft-only. See CLEAR WIPES THE DRAFT above — this does not close and does
  // not commit.
  const handleClear = () => setDraft({ primary: null, secondary: null });

  // The host owns the close: its handler commits and then pops the sentinel, so
  // Apply resolves through the SAME single navigate(-1) that Cancel, ✕, Escape
  // and hardware back do. This component never navigates.
  const handleApply = () => onApply(draft);

  return (
    <div
      className="fsm"
      data-testid="field-group-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Group"
    >
      <div className="fsm-header">
        <h2 className="fsm-title">Group</h2>
        <button
          type="button"
          className="fsm-bulk"
          data-testid="field-group-clear"
          onClick={handleClear}
          disabled={draft.primary === null}
        >
          Clear
        </button>
        <button
          type="button"
          className="fsm-close"
          data-testid="field-group-close"
          onClick={onClose}
          aria-label="Close group"
        >
          ✕
        </button>
      </div>

      <div className="fsm-body">
        {!firstKey && (
          /* Sort shows its empty state when the LIST is empty; there is no list
             here and the primary picker always renders, so the only empty state
             this modal has is the one where the view offers nothing at all. */
          <p className="fsm-empty" data-testid="field-group-empty">
            No fields to group by in this view.
          </p>
        )}

        {firstKey && (
          <div className="fsm-level" data-testid="field-group-level-primary">
            {/* The ordinal is the nesting depth, not a sort precedence: 1 is the
                outer bucket, 2 the inner. Same badge Sort uses. */}
            <span className="fsm-level-ord" aria-hidden="true">1</span>
            <select
              className="fsm-select"
              data-testid="field-group-primary"
              aria-label="Primary group field"
              value={draft.primary ?? UNSET}
              onChange={(e) => changePrimary(e.target.value)}
            >
              <option value={UNSET}>No grouping</option>
              {/* raw null, NOT ?? '' — see the docblock's note on the hatch */}
              <FieldPickerOptions
                fields={fields}
                hidden={hidden}
                value={draft.primary}
              />
            </select>
          </div>
        )}

        {firstKey && draft.primary !== null && (
          <div className="fsm-level" data-testid="field-group-level-secondary">
            <span className="fsm-level-ord" aria-hidden="true">2</span>
            <select
              className="fsm-select"
              data-testid="field-group-secondary"
              aria-label="Secondary group field"
              value={draft.secondary ?? UNSET}
              onChange={(e) => changeSecondary(e.target.value)}
            >
              <option value={UNSET}>None</option>
              <FieldPickerOptions
                fields={fields}
                hidden={hidden}
                value={draft.secondary}
                exclude={draft.primary}
              />
            </select>
          </div>
        )}
      </div>

      <div className="fsm-footer">
        <button
          type="button"
          className="fsm-cancel"
          data-testid="field-group-cancel"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="fsm-apply"
          data-testid="field-group-apply"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
}
